import { type RefObject, useCallback } from "react";
import { toast } from "@/components/ui/toast";
import {
  filenameSlugForComponentType,
  filenameTimestamp,
} from "@/lib/utils/aiChatUtils";
import { downloadCsv, formatCsvValue } from "@/lib/utils/csv";

export interface UseExportRenderedTableAsCsvDeps {
  /** Container whose first `<table>` is exported; no-op when `current` is `null`. */
  targetRef: RefObject<HTMLElement | null>;
  /** Catalog type in PascalCase; used in the download filename. */
  componentType: string;
}

export interface UseExportRenderedTableAsCsvReturn {
  /** Builds CSV from the rendered table (header labels, current row order) and downloads it. */
  exportAsCsv: () => void;
}

const readTableHeaderLabel = (th: Element): string => {
  const probe = th.cloneNode(true) as HTMLElement;
  for (const svg of probe.querySelectorAll("svg")) {
    svg.remove();
  }
  return (probe.textContent ?? "").trim();
};

/** Full http(s) href of a link cell (e.g. an Article headline), else "". */
const readCellHref = (td: Element | undefined): string => {
  const href = td?.querySelector("a[href]")?.getAttribute("href")?.trim() ?? "";
  return href.startsWith("http://") || href.startsWith("https://") ? href : "";
};

const toCsvLine = (cells: readonly string[]): string =>
  cells.map(formatCsvValue).join(",");

/** CSV of the rendered table; columns with link cells gain a companion `<col> URL` column. */
const buildCsvFromRenderedTable = (table: HTMLTableElement): string => {
  const headerCells = Array.from(table.querySelectorAll("thead th"));
  const columns = headerCells.map(readTableHeaderLabel);

  const rows = Array.from(table.querySelectorAll("tbody tr")).map((row) =>
    Array.from(row.querySelectorAll("td")),
  );

  const columnHasLink = columns.map((_, i) =>
    rows.some((cells) => readCellHref(cells[i]) !== ""),
  );

  const headerLine = columns.flatMap((col, i) =>
    columnHasLink[i] ? [col, `${col} URL`] : [col],
  );

  const dataLines = rows.map((cells) =>
    columns.flatMap((_, i) => {
      const text = cells[i]?.textContent?.trim() ?? "";
      return columnHasLink[i] ? [text, readCellHref(cells[i])] : [text];
    }),
  );

  return [headerLine, ...dataLines].map(toCsvLine).join("\n");
};

/**
 * Exports the first `<table>` under `targetRef` as a timestamped CSV download
 * (matches visible DOM order, e.g. user-sorted Herald chat tables).
 */
export const useExportRenderedTableAsCsv = ({
  targetRef,
  componentType,
}: UseExportRenderedTableAsCsvDeps): UseExportRenderedTableAsCsvReturn => {
  const exportAsCsv = useCallback(() => {
    const node = targetRef.current;
    if (node === null) {
      return;
    }
    const table = node.querySelector("table");
    if (!(table instanceof HTMLTableElement)) {
      toast.error("No table data found to export");
      return;
    }
    try {
      const csv = buildCsvFromRenderedTable(table);
      downloadCsv(
        csv,
        `chat-${filenameSlugForComponentType(componentType)}-${filenameTimestamp()}.csv`,
      );
    } catch (err) {
      console.error("[useExportRenderedTableAsCsv] CSV export failed", err);
      toast.error("Could not export CSV");
    }
  }, [targetRef, componentType]);

  return { exportAsCsv };
};
